import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { 
  Wallet, 
  Coins, 
  CreditCard, 
  Star, 
  TrendingUp,
  AlertCircle,
  CheckCircle,
  Globe,
  Gift
} from 'lucide-react';
import { 
  UserWallet, 
  PurchaseTier, 
  UserLocation 
} from '../types/firebase';
import { 
  walletService, 
  transactionService,
  locationService,
  PURCHASE_TIERS,
  CHAPTER_PRICING
} from '../services/firebase';
// Removed payment integrations
import Navbar from './Navbar';
import Footer from './Footer';
import './WalletPage.css';

interface WalletPageProps {
  className?: string;
}

const WalletPage: React.FC<WalletPageProps> = ({ className = '' }) => {
  const { user, loading: authLoading } = useAuth();
  const [wallet, setWallet] = useState<UserWallet | null>(null);
  const [location, setLocation] = useState<UserLocation | null>(null);
  const [purchaseTiers, setPurchaseTiers] = useState<PurchaseTier[]>([]);
  const [chapterPrice, setChapterPrice] = useState<number>(0);
  const [purchasing, setPurchasing] = useState(false);
  const [selectedTier, setSelectedTier] = useState<PurchaseTier | null>(null);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [notification, setNotification] = useState<{type: 'success' | 'error', message: string} | null>(null);

  useEffect(() => {
    if (!authLoading && user) {
      loadWalletData();
    }
  }, [authLoading, user]);

  const loadWalletData = async () => {
    if (!user) return;
    
    try {
      // Load user location with fallback
      let locationData;
      try {
        locationData = await locationService.getUserLocation();
        console.log('Location loaded successfully:', locationData);
      } catch (locationError) {
        console.warn('Location service failed, using default:', locationError);
        locationData = { country: 'India', countryCode: 'IN', currency: 'INR', ip: 'unknown' };
      }
      setLocation(locationData);

      // Load purchase tiers and pricing (these are local operations, won't fail)
      const tiers = locationService.getPurchaseTiers(locationData.countryCode);
      setPurchaseTiers(tiers);
      const price = locationService.getChapterPrice(locationData.countryCode);
      setChapterPrice(price);
      console.log('Purchase tiers and pricing loaded:', { tiers: tiers.length, price });

      // Load wallet with specific error handling
      try {
        console.log('Attempting to load wallet for user:', user.uid);
        const walletData = await walletService.getOrCreateWallet(user.uid);
        setWallet(walletData);
        console.log('Wallet loaded successfully:', walletData);
      } catch (walletError) {
        console.error('Wallet creation/loading failed:', walletError);
        showNotification('error', 'Failed to access wallet. Please try refreshing the page.');
        return;
      }

    } catch (error) {
      console.error('General error loading wallet data:', error);
      showNotification('error', 'Failed to load wallet data');
    }
  };

  const showNotification = (type: 'success' | 'error', message: string) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 5000);
  };

  const handlePurchaseVos = async (tier: PurchaseTier) => {
    if (!user || purchasing || !location) return;

    // Payment functionality has been disabled
    showNotification('error', 'Payment functionality is currently disabled');
  };

  const openPurchaseModal = (tier: PurchaseTier) => {
    setSelectedTier(tier);
    setShowPurchaseModal(true);
  };

  const formatCurrency = (amount: number, currency: string) => {
    return currency === 'INR' ? `₹${amount}` : `$${amount}`;
  };

  if (!user) {
    return (
      <div className={`wallet-page ${className}`}>
        <Navbar />
        <div className="wallet-container">
          <div className="login-prompt">
            <h2>Please log in to view your wallet</h2>
            <p>Sign in to manage your vos balance and purchase premium chapters.</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className={`wallet-page ${className}`}>
      <Navbar />
      
      {/* Notification */}
      <AnimatePresence>
        {notification && (
          <motion.div
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -50 }}
            className={`notification ${notification.type}`}
          >
            {notification.type === 'success' ? <CheckCircle size={20} /> : <AlertCircle size={20} />}
            <span>{notification.message}</span>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="wallet-container">
        {/* Wallet Header */}
        <motion.div
          className="wallet-header"
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="wallet-info">
            <h1>
              <Wallet size={32} />
              Your Wallet
            </h1>
            <p>Manage your vos balance and purchase premium chapters</p>
          </div>
          
          <div className="location-info">
            <Globe size={16} />
            <span>{location?.country}</span>
          </div>
        </motion.div>

        {/* Wallet Balance */}
        <motion.div
          className="wallet-balance"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <div className="balance-card">
            <div className="balance-icon">
              <Coins size={48} />
            </div>
            <div className="balance-info">
              <h2>{wallet?.balance?.toFixed(2) || '0.00'}</h2>
              <p>vos Balance</p>
            </div>
          </div>
          
          <div className="balance-stats">
            <div className="stat-item">
              <Star size={20} />
              <span>Chapter Price: {chapterPrice} vos</span>
            </div>
            <div className="stat-item">
              <TrendingUp size={20} />
              <span>Chapters Available: {Math.floor((wallet?.balance || 0) / chapterPrice)}</span>
            </div>
          </div>
        </motion.div>

        {/* Purchase Tiers */}
        <motion.div
          className="purchase-section"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <h2>Purchase Vos</h2>
          <p>Buy vos for your wallet. Higher tiers come with bonus vos!</p>
          
          <div className="tiers-grid">
            {purchaseTiers.map((tier) => (
              <motion.div
                key={tier.id}
                className={`tier-card ${tier.id === 2 ? 'popular' : ''}`}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => openPurchaseModal(tier)}
              >
                {tier.id === 2 && <div className="popular-badge">Most Popular</div>}
                
                <div className="tier-header">
                  <h3>{tier.baseVos} vos</h3>
                  <div className="tier-bonus">
                    + {tier.bonusVos} bonus vos
                  </div>
                </div>
                
                <div className="tier-total">
                  <span className="total-vos">{tier.totalVos} vos</span>
                  <span className="tier-price">{formatCurrency(tier.price, tier.currency)}</span>
                </div>
                
                <div className="tier-savings">
                  {tier.bonusVos > 0 && (
                    <span className="savings-text">
                      {Math.round((tier.bonusVos / tier.baseVos) * 100)}% bonus
                    </span>
                  )}
                </div>
                
                <button className="tier-button">
                  <CreditCard size={16} />
                  Purchase
                </button>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Purchase Modal */}
      <AnimatePresence>
        {showPurchaseModal && selectedTier && (
          <motion.div
            className="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowPurchaseModal(false)}
          >
            <motion.div
              className="purchase-modal"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <h3>Confirm Purchase</h3>
              
              <div className="purchase-details">
                <div className="detail-row">
                  <span>Base vos:</span>
                  <span>{selectedTier.baseVos} vos</span>
                </div>
                <div className="detail-row bonus">
                  <span>Bonus vos:</span>
                  <span>+{selectedTier.bonusVos} vos</span>
                </div>
                <div className="detail-row total">
                  <span>Total vos:</span>
                  <span>{selectedTier.totalVos} vos</span>
                </div>
                <div className="detail-row price">
                  <span>Price:</span>
                  <span>{formatCurrency(selectedTier.price, selectedTier.currency)}</span>
                </div>
              </div>
              
              <div className="modal-actions">
                <button
                  className="cancel-button"
                  onClick={() => setShowPurchaseModal(false)}
                  disabled={purchasing}
                >
                  Cancel
                </button>
                <button
                  className="confirm-button"
                  onClick={() => handlePurchaseVos(selectedTier)}
                  disabled={purchasing}
                >
                  {purchasing ? 'Processing...' : 'Confirm Purchase'}
                </button>
              </div>
              
              <div className="payment-note">
                <AlertCircle size={16} />
                <span>
                  Payment functionality is currently disabled
                </span>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <Footer />
    </div>
  );
};

export default WalletPage;